import { useTranslations } from 'next-intl';
import { buildMagazinePageHref } from '../helpers/buildMagazinePageHref';
import type { MagazineArticle } from '../helpers/magazineCategory';
import { MagazineArticleGrid } from '../MagazineArticleGrid/MagazineArticleGrid';
import { MagazinePagination } from '../MagazinePagination/MagazinePagination';
import { MagazineSearchHeader } from '../MagazineSearchHeader/MagazineSearchHeader';

type MagazineSearchPageProps = {
  query?: string;
  pageNumber?: number;
  articles: readonly MagazineArticle[];
  totalPages: number;
  baseImgUrl: string;
};

function MagazineSearchPage({ query = '', pageNumber = 1, articles, totalPages, baseImgUrl }: MagazineSearchPageProps) {
  const t = useTranslations('magazine.search_results');
  const trimmedQuery = query.trim();
  const hasResults = trimmedQuery.length > 0 && articles.length > 0;

  return (
    <div className="flex flex-col" data-testid="magazine-search-page">
      <div data-testid="magazine-search-page-track" data-track-container="Search Results" {...(trimmedQuery ? { 'data-track-text': trimmedQuery } : {})}>
        <MagazineSearchHeader />
        {hasResults ? (
          <div className="mt-8 flex flex-col gap-8">
            <p className="paragraph text-foreground" data-testid="magazine-search-page-query">
              {t.rich('query_label', {
                query: trimmedQuery,
                strong: chunks => (
                  <span className="paragraph-strong" data-testid="magazine-search-page-query-term">
                    {chunks}
                  </span>
                ),
              })}
            </p>
            <div data-testid="magazine-search-results">
              <MagazineArticleGrid articles={articles} baseImgUrl={baseImgUrl} currentPage={pageNumber} priorityFirstCard={pageNumber === 1} />
            </div>
          </div>
        ) : (
          <p
            className="paragraph mt-6 py-4 text-center text-foreground"
            data-testid={trimmedQuery ? 'magazine-search-page-no-results' : 'magazine-search-page-empty'}
            {...(trimmedQuery && { 'data-track-container': 'No Results' })}
          >
            {trimmedQuery ? (
              <>
                <span className="block">{t('no_results_title')}</span>
                <span className="block text-foreground-subtle">{t('no_results_hint')}</span>
              </>
            ) : (
              t('empty')
            )}
          </p>
        )}
      </div>
      {hasResults && (
        <MagazinePagination
          className="mt-8"
          currentPage={pageNumber}
          totalPages={totalPages}
          getPageHref={page => buildMagazinePageHref({ type: 'search', query: trimmedQuery }, page)}
        />
      )}
    </div>
  );
}

export { MagazineSearchPage, type MagazineSearchPageProps };
